import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { fetchSOWList } from '../services/api';
import { isValueEmpty } from '../services/utils';
import { useAppStore } from './useAppStore';
import { createTicketScopedPersistence } from './ticketScopedPersistence';

// Follows the ticket in the URL, including in-app switches that never reload the page.
// The list belongs to the technician rather than to one ticket, so a switch keeps the
// in-memory list (Home must still show it offline) and only adopts the new ticket's
// cached record when one exists.
const ticketPersistence = createTicketScopedPersistence({
  prefix: 'sow-list-data-storage-',
  persistedFields: ['sowListData', 'sowListLastUpdated'],
  resetState: {}
});

export const useSOWListDataStore = create(
  persist(
    (set, get) => ({
      sowListData: null,
      sowListLoading: false,
      sowListError: null,
      sowListLastUpdated: null,

      // Fetch data from backend
      fetchSOWListData: async () => {
        const pendingSwitch = ticketPersistence.followTicket(useSOWListDataStore);
        if (pendingSwitch) await pendingSwitch;
        const { isOnline } = useAppStore.getState();
        
        if (get().sowListLoading) {
          return get().sowListData;
        }

        if (!isOnline && !isValueEmpty(get().sowListData)) {
            return get().sowListData;
        }
        
        set({ sowListLoading: true, sowListError: null });
        
        try {
          const data = await fetchSOWList();
          set({ 
            sowListData: data,
            sowListLoading: false,
            sowListError: null,
            sowListLastUpdated: new Date().toISOString()
          });
          return data;
        } catch (error) {
          set({
            sowListError: error.message,
            sowListLoading: false 
          });
          throw error;
        }
      },

      // Get current SOW List data
      getSOWListData: () => {
        return get().sowListData;
      },

      // Replace entire SOW data object
      setSOWListData: (newData) => {
        set({ 
          sowListData: newData,
          sowListLastUpdated: new Date().toISOString()
        });
      },

      // Clear all SOW data
      clearSOWListData: () => {
        set({ 
          sowListData: null,
          sowListError: null,
          sowListLastUpdated: null 
        });
      },
      
      // The list is technician-wide and kept in memory across ticket switches, but it is
      // persisted per ticket, so the store must follow the current ticket before the write
      // or the scoped storage drops it.
      updateTaskStatus: (issueKey, newStatus) => {
        const applyUpdate = (sowListData) => {
          if (!sowListData) {
            console.warn('No SOW list data available');
            return;
          }

          set({
            sowListData: sowListData.map(task =>
              task.workorderKey === issueKey
                ? { ...task, tasksStatus: newStatus }
                : task
            ),
            sowListLastUpdated: new Date().toISOString()
          });
        };

        const listBeforeSwitch = get().sowListData;
        const pendingSwitch = ticketPersistence.followTicket(useSOWListDataStore);
        if (!pendingSwitch) return applyUpdate(listBeforeSwitch);
        return pendingSwitch.then(() => applyUpdate(listBeforeSwitch ?? get().sowListData));
      }

    }),
    // Partitioned per ticket — see ticketScopedPersistence.
    {
      ...ticketPersistence.persistOptions,
      // The list is technician-wide: a ticket's older cached copy must not replace a
      // fresher list already in memory (e.g. one carrying an offline-completed status).
      merge: (persisted, current) => {
        const merged = ticketPersistence.persistOptions.merge(persisted, current);
        const isMemoryFresher = current.sowListData && (current.sowListLastUpdated ?? '') >= (persisted?.sowListLastUpdated ?? '');
        return isMemoryFresher
          ? { ...merged, sowListData: current.sowListData, sowListLastUpdated: current.sowListLastUpdated }
          : merged;
      },
    }
  )
);